import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { TitleWithNav } from "common/TitleWithNav";
import Switch from "@mui/material/Switch";
import Modal from "common/Modal";
import CollapsablePanel from "common/layouts/CollapsablePanel";
import { isPolicyNameValid } from "../../Rules/utils";
import { safeStr, checkNothingPending } from "common/api";
import CheckBox from "common/Checkbox";
import { responsePolicyMap, DEFAULT_POLICY } from "./utils";
import {
  getPolicies,
  BlockModal,
  parsePolicy,
  getPolicy,
  setBrateValue,
  setBdurValue,
  setBthrValue,
  setBthrwValue,
  SSHRATE_DFLT_VAL,
  SSHZERO_DFLT_VAL,
  SSHBTHRW_DFLT_VAL,
} from "../../PolicyEdit/utils";
import NumberInputWithSpinner from "common/NumberInputWithSpinnerState";
import * as d3 from "d3";

const Wrapper = styled.div`
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const ChangesWrapper = styled.div`
  padding: 0;
`;

const PendingChanges = styled.div`
  display: flex;
  margin-top: 7px;
`;

const Circle = styled.div`
  height: 16px;
  width: 16px;
  background: #f44336;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
`;

const PolicyFlowEdit = ({ returnView, tab, policy }) => {
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [initPolicy, setInitPolicy] = useState(DEFAULT_POLICY);
  const [policies, setPolicies] = useState({});
  const [editedPolicy, setEditedPolicy] = useState(DEFAULT_POLICY);

  useEffect(() => {
    getPolicies("flow")
      .then((response) => {
        setPolicies(response);
        if (policy) {
          const editPolicy = response[policy];
          if (!editPolicy) {
            showModalError("Error:", `Unknow policy ${policy}`);
          } else {
            return getPolicy(policy);
          }
        }
      })
      .then((response) => {
        if (response) {
          let parsedPolicy = parsePolicy(response, responsePolicyMap);
          parsedPolicy.optEnabled = parsedPolicy.opt ? true : false;
          parsedPolicy.opt = parsedPolicy.opt
            ? Number(100 - parsedPolicy.opt).toFixed(2) // IEEE754 precision fix
            : 0;
          if (
            parsedPolicy.quotad === "block" &&
            parsedPolicy.quotau === "block"
          ) {
            parsedPolicy.quotaLimit = false;
          } else {
            parsedPolicy.quotaLimit = true;
          }
          if (parsedPolicy.shdrate) {
            parsedPolicy.shdrateUnit = parsedPolicy.shdrate.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shdrate = parseFloat(parsedPolicy.shdrate);
          }
          if (parsedPolicy.shurate) {
            parsedPolicy.shurateUnit = parsedPolicy.shurate.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shurate = parseFloat(parsedPolicy.shurate);
          }
          if (parsedPolicy.shdbrate) {
            parsedPolicy.shdbrateUnit = parsedPolicy.shdbrate.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shdbrate = parseFloat(parsedPolicy.shdbrate);
          }
          if (parsedPolicy.shubrate) {
            parsedPolicy.shubrateUnit = parsedPolicy.shubrate.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shubrate = parseFloat(parsedPolicy.shubrate);
          }
          if (parsedPolicy.shdbthr) {
            parsedPolicy.shdbthrUnit = parsedPolicy.shdbthr.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shdbthr = parseFloat(parsedPolicy.shdbthr);
          }
          if (parsedPolicy.shubthr) {
            parsedPolicy.shubthrUnit = parsedPolicy.shubthr.endsWith("%")
              ? "%"
              : "mbps";
            parsedPolicy.shubthr = parseFloat(parsedPolicy.shubthr);
          }
          if (parsedPolicy.shapdflowrate) {
            parsedPolicy.shapdflowrateUnit =
              parsedPolicy.shapdflowrate.endsWith("%") ? "%" : "mbps";
            parsedPolicy.shapdflowrate = parseFloat(parsedPolicy.shapdflowrate);
          }
          if (parsedPolicy.shapuflowrate) {
            parsedPolicy.shapuflowrateUnit =
              parsedPolicy.shapuflowrate.endsWith("%") ? "%" : "mbps";
            parsedPolicy.shapuflowrate = parseFloat(parsedPolicy.shapuflowrate);
          }
          const initPolicyNew = { ...DEFAULT_POLICY, ...parsedPolicy };
          setInitPolicy(initPolicyNew);
          setEditedPolicy(initPolicyNew);
        }
      });
  }, []);

  const compareValues = (policyA, policyB) => {
    return Object.entries(policyA).find(([key, value]) => {
      return policyB[key] !== value;
    });
  };

  const changes =
    compareValues(editedPolicy, initPolicy) === undefined ? false : true;

  function checkRate(policy, unit, name, type) {
    if (isNaN(parseFloat(policy))) {
      showModalError("Warning:", `Invalid ${type} ${name.toLowerCase()}`);
      return false;
    } else if (unit !== "mbps") {
      if (Number(policy) < 0 || Number(policy) > 500) {
        showModalError(
          "Warning:",
          `${name} must be a percentage between 0% and 500%`
        );
        return false;
      }
    } else {
      if (Number(policy) < 0.0 || Number(policy) > SSHRATE_DFLT_VAL) {
        showModalError(
          "Warning:",
          `${name} must be between 0 and ${SSHRATE_DFLT_VAL}`
        );
        return false;
      }
    }
    return true;
  }

  const validatePolicy = (policy) => {
    if (!isPolicyNameValid(policy.name)) {
      showModalError("Warning:", "Invalid name");
      return false;
    }
    if (!policy.block) {
      if (policy.optEnabled) {
        if (Number(policy.opt) < 0.00 || Number(policy.opt) > 99.99) {
          showModalError("Warning:", "Invalid tracking percentage");
          return false;
        }
      }
      if (policy.shapd) {
        const check = checkRate(
          policy.shdrate,
          policy.shdrateunit,
          "Rate Per Subscriber",
          "download"
        );
        if (!check) {
          return false;
        }
      }
      if (policy.shapdflow) {
        const check = checkRate(
          policy.shapdflowrate,
          policy.shapdflowrateunit,
          "Flow Shaping Rate",
          "download"
        );
        if (!check) {
          return false;
        }
      }
      if (policy.shapddscp) {
        if(isNaN(shapddscprate)){
          showModalError("Warning:", `Invalid Code Point Downlink DSCP`);
          return false;
        }
        if(!Number.isInteger(shapddscprate)){
          showModalError("Warning:", `Code Point Downlink DSCP should be an integer`);
          return false;
        }
      }
      if (policy.shapd) {
        const check = checkRate(
          policy.shdbrate,
          policy.shdbrateunit,
          "Burst Rate",
          "download",
          true
        );
        if (!check) {
          return false;
        }
      }
      if (
        policy.shapd &&
        (isNaN(policy.shdbdur) ||
          Number(policy.shdbdur) < 0 ||
          Number(policy.shdbdur) > 86400)
      ) {
        showModalError("Warning:", "Invalid download burst duration");
        return false;
      }
      if (policy.shapd) {
        const check = checkRate(
          policy.shdbthr,
          policy.shdbthrunit,
          "Burst Threshold",
          "download",
          true
        );
        if (!check) {
          return false;
        }
      }
      if (
        policy.shapd &&
        (isNaN(policy.shdbthrw) || Number(policy.shdbthrw) < 0)
      ) {
        showModalError("Warning:", "Invalid download burst threshold window");
        return false;
      }
      if (policy.shapu) {
        const check = checkRate(
          policy.shurate,
          policy.shurateunit,
          "Rate Per Subscriber",
          "upload"
        );
        if (!check) {
          return false;
        }
      }
      if (policy.shapuflow) {
        const check = checkRate(
          policy.shapuflowrate,
          policy.shapuflowrateunit,
          "Flow Shaping Rate",
          "upload"
        );
        if (!check) {
          return false;
        }
      }
      if (policy.shapudscp) {
        if(isNaN(shapudscprate)){
          showModalError("Warning:", `Invalid Code Point Uplink DSCP`);
          return false;
        }
        if(!Number.isInteger(shapudscprate)){
          showModalError("Warning:", `Code Point Uplink DSCP should be an integer`);
          return false;
        }
      }
      if (policy.shapu) {
        const check = checkRate(
          policy.shubrate,
          policy.shubrateunit,
          "Burst Rate",
          "upload",
          true
        );
        if (!check) {
          return false;
        }
      }
      if (
        policy.shapu &&
        (isNaN(policy.shubdur) ||
          Number(policy.shubdur) < 0 ||
          Number(policy.shubdur) > 86400)
      ) {
        showModalError("Warning:", "Invalid upload burst duration");
        return false;
      }
      if (policy.shapu) {
        const check = checkRate(
          policy.shubthr,
          policy.shubthrunit,
          "Burst Threshold",
          "upload",
          true
        );
        if (!check) {
          return false;
        }
      }
      if (
        policy.shapu &&
        (isNaN(policy.shubthrw) || Number(policy.shubthrw) < 0)
      ) {
        showModalError("Warning:", "Invalid upload burst threshold window");
        return false;
      }
      if (policy.priority) {
        if (isNaN(policy.priority)) {
          showModalError("Warning:", "Invalid priority");
          return false;
        } else if (
          !Number.isInteger(Number(policy.priority)) ||
          Number(policy.priority) < -5 ||
          Number(policy.priority) > 5
        ) {
          showModalError(
            "Warning:",
            "Shaping priority must be an integer betwen -5 and 5"
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleApply = () => {
    let cmdClear;
    const newPolicy = {
      name,
      block,
      optEnabled,
      opt,
      priority,
      shapd,
      shdrate,
      shdrateunit: shdrateUnit,
      shdbrate,
      shdbrateunit: shdbrateUnit,
      shdbdur,
      shdbthr,
      shdbthrunit: shdbthrUnit,
      shdbthrw,
      shapdflow,
      shapdflowrate,
      shapdflowrateunit: shapdflowrateUnit,
      shapddscp,
      shapddscprate,
      shapu,
      shurate,
      shurateunit: shurateUnit,
      shubrate,
      shubrateunit: shubrateUnit,
      shubdur,
      shubthr,
      shubthrunit: shubthrUnit,
      shubthrw,
      shapuflow,
      shapuflowrate,
      shapuflowrateunit: shapuflowrateUnit,
      shapudscp,
      shapudscprate,
      skiprate,
      skipsubsgrouprate,
      quota,
      quotaLimit,
      quotad,
      quotau,
      droptcp,
      dropudp,
      dropother,
    };

    if (!validatePolicy(newPolicy)) return;

    if (policy) {
      if (policy !== newPolicy.name) {
        const policiesSet = new Set(Object.keys(policies));
        if (policiesSet.has(newPolicy.name)) {
          showModalError("Error:", "Duplicated policy name");
          return;
        }
        cmdClear = `clear policy flow ${safeStr(policy)}`;
      }
    }
    const twoDecimals = d3.format(".2f");
    let cmd = `set policy flow ${safeStr(newPolicy.name)}`;
    if (newPolicy.block) {
      cmd +=
        " off on off on off off " +
        "off default default default default default " +
        "off default default default default default off off off off " +
        `off off default off default on block block ${newPolicy.priority} off default off default`;
    } else {
      if (!newPolicy.optEnabled) {
        cmd += " 0.0 on off off on off";
      } else {
        cmd += ` ${
          newPolicy.opt === 0 || newPolicy.opt === '0.00' ? 'on' : twoDecimals(100 - newPolicy.opt)
        } ${
          newPolicy.opt ===  0 || newPolicy.opt === '0.00' ? "off" : "on"
        }`;
        cmd += " off off on off";
      }
      if (newPolicy.shapd) {
        cmd += ` on ${
          newPolicy.shdrateunit === "mbps"
            ? Math.floor(Number(newPolicy.shdrate) * 1000).toString()
            : parseFloat(newPolicy.shdrate)
        }${newPolicy.shdrateunit === "mbps" ? "" : "%"} ${
          newPolicy.shdbrateunit === "mbps"
            ? setBrateValue(newPolicy.shdbrate)
            : parseFloat(newPolicy.shdbrate)
        }${newPolicy.shdbrateunit === "mbps" ? "" : "%"} ${setBdurValue(
          newPolicy.shdbdur
        )} ${
          newPolicy.shdbthrunit === "mbps"
            ? setBthrValue(newPolicy.shdbthr)
            : parseFloat(newPolicy.shdbthr)
        }${newPolicy.shdbthrunit === "mbps" ? "" : "%"} ${setBthrwValue(
          newPolicy.shdbthrw
        )}`;
      } else {
        //       SHPD
        cmd += " off default default default default default";
      }

      if (newPolicy.shapu) {
        //       SHPU  SHUR SHUBR SHUBD SHUBT
        cmd += ` on ${
          newPolicy.shurateunit === "mbps"
            ? Math.floor(Number(newPolicy.shurate) * 1000).toString()
            : parseFloat(newPolicy.shurate)
        }${newPolicy.shurateunit === "mbps" ? "" : "%"} ${
          newPolicy.shubrateunit === "mbps"
            ? setBrateValue(newPolicy.shubrate)
            : parseFloat(newPolicy.shubrate)
        }${newPolicy.shubrateunit === "mbps" ? "" : "%"} ${setBdurValue(
          newPolicy.shubdur
        )} ${
          newPolicy.shubthrunit === "mbps"
            ? setBthrValue(newPolicy.shubthr)
            : parseFloat(newPolicy.shubthr)
        }${newPolicy.shubthrunit === "mbps" ? "" : "%"} ${setBthrwValue(
          newPolicy.shubthrw
        )}`;
      } else {
        //       SHPU
        cmd += " off default default default default default";
      }

      if (newPolicy.skiprate) {
        //       SKIP_RATE
        cmd += " on";
      } else {
        //       SKIP_RATE
        cmd += " off";
      }
      if (newPolicy.skipsubsgrouprate) {
        //       SKIP_RATE
        cmd += " on";
      } else {
        //       SKIP_RATE
        cmd += " off";
      }

      if (droptcp) {
        cmd += " on";
      } else {
        cmd += " off";
      }
      if (dropudp) {
        cmd += " on";
      } else {
        cmd += " off";
      }
      if (dropother) {
        cmd += " on";
      } else {
        cmd += " off";
      }

      // shaping-flow parameters
      if (newPolicy.shapdflow) {
        cmd += ` on ${
          newPolicy.shapdflowrateunit === "mbps"
            ? Math.floor(Number(newPolicy.shapdflowrate) * 1000).toString()
            : parseFloat(newPolicy.shapdflowrate)
        }${newPolicy.shapdflowrateunit === "mbps" ? "" : "%"}`;
      } else {
        cmd += " off default";
      }
      if (newPolicy.shapuflow) {
        cmd += ` on ${
          newPolicy.shapuflowrateunit === "mbps"
            ? Math.floor(Number(newPolicy.shapuflowrate) * 1000).toString()
            : parseFloat(newPolicy.shapuflowrate)
        }${newPolicy.shapuflowrateunit === "mbps" ? "" : "%"}`;
      } else {
        cmd += " off default";
      }

      //quota parameters
      if (
        newPolicy.quota &&
        newPolicy.quotaLimit &&
        !Number.isNaN(parseFloat(newPolicy.quotad)) &&
        !Number.isNaN(parseFloat(newPolicy.quotad))
      ) {
        const down = Math.floor(Number(newPolicy.quotad) * 1000).toString();
        const up = Math.floor(Number(newPolicy.quotau) * 1000).toString();
        cmd += " on " + down + " " + up;
      } else if (newPolicy.quota && !newPolicy.quotaLimit) {
        cmd += " on block block";
      } else {
        cmd += " off block block";
      }

      cmd += " " + newPolicy.priority;

      // dscp parameters
      if (newPolicy.shapddscp) {
        cmd += ` on ${shapddscprate}`;
      } else {
        cmd += " off default";
      }
      if (newPolicy.shapudscp) {
        cmd += ` on ${shapudscprate}`;
      } else {
        cmd += " off default";
      }
    }
    checkNothingPending()
      .then(() => {
        if (cmdClear) {
          return ifCl.run(cmdClear);
        } else {
          return;
        }
      })
      .then(() => ifCl.run(cmd))
      .then((response) => {
        globalNavigateBack();
      })
      .catch((error) => {
        const updateNameError = error.match(
          /%ERR-ENOENT: Cannot remove policy "(.*)" because it is part of a rule/
        );
        if (updateNameError) {
          showModalError(
            "Error:",
            "Cannot update name policy because it is part of a rule."
          );
        } else {
          showModalError("Error:", error);
        }
      });
  };

  const handleBlock = () => {
    if (!block) {
      setShowBlockModal(true);
    } else {
      setEditedPolicy({ ...editedPolicy, ["block"]: false });
    }
  };

  const {
    name,
    block,
    optEnabled,
    opt,
    priority,
    shapd,
    shdrate,
    shdrateUnit,
    shdbrate,
    shdbrateUnit,
    shdbdur,
    shdbthr,
    shdbthrUnit,
    shdbthrw,
    shapdflow,
    shapdflowrate,
    shapdflowrateUnit,
    shapu,
    shurate,
    shurateUnit,
    shubrate,
    shubrateUnit,
    shubdur,
    shubthr,
    shubthrUnit,
    shubthrw,
    shapuflow,
    shapuflowrate,
    shapuflowrateUnit,
    skiprate,
    skipsubsgrouprate,
    quota,
    quotaLimit,
    quotad,
    quotau,
    droptcp,
    dropudp,
    dropother,
    shapddscp,
    shapddscprate,
    shapudscp,
    shapudscprate,
  } = editedPolicy;

  return (
    <Wrapper>
      <div className="row clearfix">
        <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
          <div className="card container-lg">
            <div className="header block-header">
              <TitleWithNav returnView={returnView}>
                <div>
                  <h2>{`${
                    policy && policy !== "" ? "Edit" : "New"
                  } Subscriber Flow Policy`}</h2>
                  <small>
                    Press <code>Apply </code> button at the end of the page to
                    set the new configuration values.
                  </small>
                </div>
              </TitleWithNav>
            </div>
            <div className="body table-responsive padding-b-0">
              <div className="row margin-t-10">
                <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-0">
                  <div className="input-group">
                    <span className="input-group-addon" title="Policy name">
                      Name
                    </span>
                    <input
                      id="poliFlowName"
                      type="text"
                      size="55"
                      style={{ borderBottom: "1px solid #ddd" }}
                      className="align-left"
                      onChange={({ target }) =>
                        setEditedPolicy({
                          ...editedPolicy,
                          ["name"]: target.value,
                        })
                      }
                      value={name}
                    />
                  </div>
                </div>
                <div className="margin-0 padding-0">
                  <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                    <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0 f-dpi-per-user">
                      <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                        <div
                          className="input-group"
                          style={{ paddingTop: "5px" }}
                        >
                          <span
                            className="input-group-addon align-left"
                            title="Block traffic"
                          >
                            Block:{" "}
                            <span className="font-bold">
                              {block ? "enabled" : "disabled"}
                            </span>
                          </span>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                        <Switch
                          className="sw-input"
                          checked={block}
                          onChange={handleBlock}
                        />
                      </div>
                      <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                    </div>
                  </div>
                </div>
                {block ? null : (
                  <Wrapper className="margin-0 padding-0">
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                      {/* <!-- ************ Column A ************ --> */}
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        {/* <!-- TCP OPTIMIZATION --> */}
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          id="poliFlowTcpSection"
                          style={{
                            border: "1px solid rgb(192, 192, 192)",
                            padding: "10px",
                          }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Optimization"
                              >
                                TCP Optimization:{" "}
                                <span className="font-bold">
                                  {optEnabled ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={optEnabled}
                              onChange={({ target }) => {
                                if (target.checked) {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["optEnabled"]: target.checked,
                                    ["opt"]: parseFloat(opt) === 0 ? DEFAULT_POLICY.opt : opt,
                                  });
                                } else {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["optEnabled"]: target.checked,
                                  });
                                }
                              }}
                            />
                          </div>
                          {optEnabled ? (
                            <div className="margin-0 padding-0">
                              <div className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0">
                                <CollapsablePanel title="TCP Advanced Parameters">
                                  <NumberInputWithSpinner
                                    name="opt"
                                    label="TCP tracking % (0-99.99%)"
                                    min={0}
                                    max={99.99}
                                    step={0.01}
                                    initialValue={opt}
                                    formatInput={(d) => d3.format(".2f")(d)}
                                    units="%"
                                    onChange={(value) =>
                                      setEditedPolicy({
                                        ...editedPolicy,
                                        ["opt"]: value,
                                      })
                                    }
                                    onReset={() => {
                                      setEditedPolicy({
                                        ...editedPolicy,
                                        ["opt"]: DEFAULT_POLICY.opt,
                                      });
                                    }}
                                  />
                                </CollapsablePanel>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                      </div>
                      {/* <!-- ************ Column B ************ --> */}
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        <NumberInputWithSpinner
                          name="priority"
                          label="Shaping Priority (-5 to +5)"
                          min={-5}
                          max={5}
                          initialValue={priority}
                          formatInput={(d) => d3.format(".2f")(d)}
                          onChange={(value) =>
                            setEditedPolicy({
                              ...editedPolicy,
                              ["priority"]: value,
                            })
                          }
                          onReset={() => {
                            setEditedPolicy({
                              ...editedPolicy,
                              ["priority"]: DEFAULT_POLICY.priority,
                            });
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                      {/* <!-- ************ Column A ************ --> */}
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        {/* <!-- Downlink shaping per Subscriber: --> */}
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Downlink shaping subscriber"
                              >
                                Downlink shaping per Subscriber:{" "}
                                <span id="statusDownlink" className="font-bold">
                                  {shapd ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={shapd}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["shapd"]: target.checked,
                                })
                              }
                            />
                          </div>

                          {shapd ? (
                            <div className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                              <NumberInputWithSpinner
                                name="shdrate"
                                label="Max rate per Subscriber"
                                min={0.0}
                                max={100000}
                                initialValue={shdrate}
                                step={0.001}
                                formatInput={(d) => d3.format(".3f")(d)}
                                className="margin-b-15"
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shdrate"]: value,
                                  })
                                }
                                hideSpinner={true}
                                units={[
                                  { label: "Mbps", value: "mbps" },
                                  { label: "% of rate policy", value: "%" },
                                ]}
                                defaultUnit="mbps"
                                selectedUnit={shdrateUnit}
                                setSelectedUnit={(value) => {
                                  if (value === "%") {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdrate"]: 100,
                                      ["shdrateUnit"]: value,
                                    });
                                  } else {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdrateUnit"]: value,
                                    });
                                  }
                                }}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shdrate"]: DEFAULT_POLICY.shdrate,
                                    ["shdrateUnit"]: DEFAULT_POLICY.shdrateUnit,
                                  });
                                }}
                              />
                              <CollapsablePanel title="Burst Options">
                                <NumberInputWithSpinner
                                  name="shdbrate"
                                  label="Burst Rate"
                                  subLabel="(0=no burst)"
                                  min={0}
                                  max={100000}
                                  step={0.001}
                                  initialValue={shdbrate}
                                  formatInput={(d) => d3.format(".3f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbrate"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shdbrateUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbrateUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbrate"]: DEFAULT_POLICY.shdbrate,
                                      ["shdbrateUnit"]:
                                        DEFAULT_POLICY.shdbrateUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shdbdur"
                                  label="Burst Duration"
                                  min={0}
                                  max={86400}
                                  initialValue={shdbdur}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbdur"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbdur"]: DEFAULT_POLICY.shdbdur,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shdbthr"
                                  label="Burst Threshold"
                                  subLabel="(0=normal rate)"
                                  min={0}
                                  max={100000}
                                  initialValue={shdbthr}
                                  step={0.001}
                                  formatInput={(d) => d3.format(".3f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbthr"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shdbthrUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbthrUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbthr"]: DEFAULT_POLICY.shdbthr,
                                      ["shdbthrUnit"]:
                                        DEFAULT_POLICY.shdbthrUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shdbthrw"
                                  label="Burst Threshold Window"
                                  min={0}
                                  max={100000}
                                  initialValue={shdbthrw}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbthrw"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shdbthrw"]: DEFAULT_POLICY.shdbthrw,
                                    });
                                  }}
                                />
                              </CollapsablePanel>
                            </div>
                          ) : null}
                        </div>
                        {/* <!-- Downlink per Flow --> */}
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Downlink shaping flow"
                              >
                                Downlink shaping per Flow:{" "}
                                <span
                                  id="statusDownlinkFlow"
                                  className="font-bold"
                                >
                                  {shapdflow ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={shapdflow}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["shapdflow"]: target.checked,
                                })
                              }
                            />
                          </div>
                          {shapdflow ? (
                            <div
                              className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0"
                              id="poliFlowShapdShowFlow"
                            >
                              <NumberInputWithSpinner
                                name="shapdflowrate"
                                label="Max rate per Flow"
                                min={0.0}
                                max={100000}
                                initialValue={shapdflowrate}
                                precision={(d) => d3.format(".3f")(d)}
                                onChange={(value) => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapdflowrate"]: value,
                                  });
                                }}
                                className="margin-b-15"
                                hideSpinner={true}
                                units={[
                                  { label: "Mbps", value: "mbps" },
                                  { label: "% of rate policy", value: "%" },
                                ]}
                                defaultUnit="mbps"
                                selectedUnit={shapdflowrateUnit}
                                setSelectedUnit={(value) => {
                                  if (value === "%") {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdflowrate"]: "100",
                                      ["shapdflowrateUnit"]: value,
                                    });
                                  } else {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapdflowrate"]:
                                        initPolicy.shapdflowrate,
                                      ["shapdflowrateUnit"]: value,
                                    });
                                  }
                                }}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapdflowrate"]:
                                      DEFAULT_POLICY.shapdflowrate,
                                    ["shapdflowrateUnit"]:
                                      DEFAULT_POLICY.shapdflowrateUnit,
                                  });
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                        <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                      </div>
                      {/* <!-- ************ Column B ************ --> */}
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        {/* <!-- Uplink shaping per Subscriber: --> */}
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          id="poliFlowUplinkSubscriberSection"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Uplink shaping"
                              >
                                Uplink shaping per Subscriber:{" "}
                                <span id="statusUplink" className="font-bold">
                                  {shapu ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-4 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={shapu}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["shapu"]: target.checked,
                                })
                              }
                            />
                          </div>
                          {shapu ? (
                            <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                              <NumberInputWithSpinner
                                name="shurate"
                                label="Max rate per Subscriber"
                                className="margin-b-15"
                                min={0.0}
                                max={100000}
                                initialValue={shurate}
                                step={0.001}
                                formatInput={(d) => d3.format(".3f")(d)}
                                units="Mbps"
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shurate"]: value,
                                  })
                                }
                                hideSpinner={true}
                                units={[
                                  { label: "Mbps", value: "mbps" },
                                  { label: "% of rate policy", value: "%" },
                                ]}
                                defaultUnit="mbps"
                                selectedUnit={shurateUnit}
                                setSelectedUnit={(value) => {
                                  if (value === "%") {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shurate"]: 100,
                                      ["shurateUnit"]: value,
                                    });
                                  } else {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shurateUnit"]: value,
                                    });
                                  }
                                }}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shurate"]: DEFAULT_POLICY.shurate,
                                    ["shurateUnit"]: DEFAULT_POLICY.shurateUnit,
                                  });
                                }}
                              />
                              <CollapsablePanel title="Burst Options">
                                <NumberInputWithSpinner
                                  name="shubrate"
                                  label="Burst Rate"
                                  subLabel="(0=no burst)"
                                  min={0}
                                  max={100000}
                                  step={0.001}
                                  initialValue={shubrate}
                                  formatInput={(d) => d3.format(".3f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubrate"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shubrateUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubrateUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubrate"]: DEFAULT_POLICY.shubrate,
                                      ["shubrateUnit"]:
                                        DEFAULT_POLICY.shubrateUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shubdur"
                                  label="Burst Duration"
                                  min={0}
                                  max={86400}
                                  initialValue={shubdur}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubdur"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubdur"]: DEFAULT_POLICY.shubdur,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shubthr"
                                  label="Burst Threshold"
                                  subLabel="(0=normal rate)"
                                  min={0}
                                  max={100000}
                                  initialValue={shubthr}
                                  step={0.001}
                                  formatInput={(d) => d3.format(".3f")(d)}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubthr"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  units={[
                                    { label: "Mbps", value: "mbps" },
                                    { label: "% of rate above", value: "%" },
                                  ]}
                                  defaultUnit="mbps"
                                  selectedUnit={shubthrUnit}
                                  setSelectedUnit={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubthrUnit"]: value,
                                    })
                                  }
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubthr"]: DEFAULT_POLICY.shubthr,
                                      ["shubthrUnit"]:
                                        DEFAULT_POLICY.shubthrUnit,
                                    });
                                  }}
                                />
                                <NumberInputWithSpinner
                                  name="shubthrw"
                                  label="Burst Threshold Window"
                                  min={0}
                                  max={100000}
                                  initialValue={shubthrw}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubthrw"]: value,
                                    })
                                  }
                                  hideSpinner={true}
                                  onReset={() => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shubthrw"]: DEFAULT_POLICY.shubthrw,
                                    });
                                  }}
                                />
                              </CollapsablePanel>
                            </div>
                          ) : null}
                        </div>
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          id="poliFlowUplinkFlowSection"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Uplink shaping Flow"
                              >
                                Uplink shaping per Flow:{" "}
                                <span
                                  id="statusUplinkFlow"
                                  className="font-bold"
                                >
                                  {shapuflow ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-4 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={shapuflow}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["shapuflow"]: target.checked,
                                })
                              }
                            />
                          </div>
                          {shapuflow ? (
                            <div className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                              <NumberInputWithSpinner
                                name="shapuflowrate"
                                label="Max rate per Flow"
                                min={0.0}
                                max={100000}
                                initialValue={shapuflowrate}
                                formatInput={(d) => d3.format(".3f")(d)}
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapuflowrate"]: value,
                                  })
                                }
                                hideSpinner={true}
                                units={[
                                  { label: "Mbps", value: "mbps" },
                                  { label: "% of rate policy", value: "%" },
                                ]}
                                defaultUnit="mbps"
                                selectedUnit={shapuflowrateUnit}
                                setSelectedUnit={(value) => {
                                  if (value === "%") {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuflowrate"]: "100",
                                      ["shapuflowrateUnit"]: value,
                                    });
                                  } else {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["shapuflowrate"]:
                                        initPolicy.shapuflowrate,
                                      ["shapuflowrateUnit"]: value,
                                    });
                                  }
                                }}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapuflowrate"]:
                                      DEFAULT_POLICY.shapuflowrate,
                                    ["shapuflowrateUnit"]:
                                      DEFAULT_POLICY.shapdflowrateUnit,
                                  });
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    {/* <!-- DSCP --> */}
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          style={{ border: "0px", padding: "0px" }}
                        >
                          <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Force Downlink DSCP"
                              >
                                Force Downlink DSCP:{" "}
                                <span
                                  id="statusDownlinkDSCP"
                                  className="font-bold"
                                >
                                  {shapddscp ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-3 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={shapddscp}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["shapddscp"]: target.checked,
                                })
                              }
                            />
                          </div>
                          {shapddscp ? (
                            <div className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                              <NumberInputWithSpinner
                                name="shapddscprate"
                                label="Code Point"
                                min={0.0}
                                max={63}
                                initialValue={shapddscprate}
                                formatInput={(d) => d3.format(".0f")(d)}
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapddscprate"]: value,
                                  })
                                }
                                units={""}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapddscprate"]:
                                      DEFAULT_POLICY.shapddscprate,
                                  });
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-lg-6 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0 padding-0 margin-t-5"
                          style={{ border: "0px", padding: "0px" }}
                        >
                                                    <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                            <div
                              className="input-group"
                              style={{ paddingTop: "5px" }}
                            >
                              <span
                                className="input-group-addon align-left"
                                title="Force Uplink DSCP"
                              >
                                Force Uplink DSCP:{" "}
                                <span
                                  id="statusUplinkDSCP"
                                  className="font-bold"
                                >
                                  {shapudscp ? "enabled" : "disabled"}
                                </span>
                              </span>
                            </div>
                          </div>
                          <div className="col-lg-4 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                            <Switch
                              className="sw-input"
                              checked={shapudscp}
                              onChange={({ target }) =>
                                setEditedPolicy({
                                  ...editedPolicy,
                                  ["shapudscp"]: target.checked,
                                })
                              }
                            />
                          </div>
                          {shapudscp ? (
                            <div className="col-lg-11 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0">
                              <NumberInputWithSpinner
                                name="shapudscprate"
                                label="Code Point"
                                min={0.0}
                                max={63}
                                initialValue={shapudscprate}
                                formatInput={(d) => d3.format(".0f")(d)}
                                onChange={(value) =>
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapudscprate"]: value,
                                  })
                                }
                                units={""}
                                onReset={() => {
                                  setEditedPolicy({
                                    ...editedPolicy,
                                    ["shapudscprate"]:
                                      DEFAULT_POLICY.shapudscprate,
                                  });
                                }}
                              />
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 margin-0 padding-0">
                        <div className="col-lg-11 col-md-11 col-sm-12 col-xs-12 margin-b-0 padding-0 margin-t-5">
                          <CollapsablePanel title="Drop Incoming Connections">
                            <div className="row">
                              <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10 margin-0">
                                TCP Connections
                              </div>
                              <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 align-right margin-0">
                                <CheckBox
                                  checked={droptcp}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["droptcp"]: value,
                                    })
                                  }
                                />
                              </div>
                              <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10 margin-0">
                                UDP Connections
                              </div>
                              <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 align-right margin-0">
                                <CheckBox
                                  checked={dropudp}
                                  onChange={(value) => {
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["dropudp"]: value,
                                    });
                                  }}
                                />
                              </div>
                              <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10 margin-0">
                                Other IP Connections
                              </div>
                              <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 align-right margin-0">
                                <CheckBox
                                  checked={dropother}
                                  onChange={(value) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["dropother"]: value,
                                    })
                                  }
                                />
                              </div>
                            </div>
                          </CollapsablePanel>
                        </div>
                        <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                      </div>
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 margin-b-0 padding-0">
                        <div
                          className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-b-0 padding-0 margin-t-5"
                          id="new-column"
                        >
                          <CollapsablePanel title="Quotas limitation &amp; counting">
                            <div className="row">
                              <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10">
                                Skip Quota:{" "}
                                <span id="statusQuota">
                                  {!quota ? "enabled" : "disabled"}
                                </span>
                              </div>
                              <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 align-right">
                                <Switch
                                  className="sw-input"
                                  checked={!quota}
                                  onChange={({ target }) =>
                                    setEditedPolicy({
                                      ...editedPolicy,
                                      ["quota"]: !target.checked,
                                    })
                                  }
                                />
                              </div>
                              {quota ? (
                                <div id="poliFlowQuotaShow">
                                  <div className="col-lg-10 col-md-10 col-sm-10 col-xs-10">
                                    When quota exceeded:{" "}
                                    <span>
                                      {quotaLimit ? "limit speed to:" : "block"}
                                    </span>
                                  </div>
                                  <div className="col-lg-2 col-md-2 col-sm-2 col-xs-2 align-right">
                                    <Switch
                                      className="sw-input"
                                      checked={quotaLimit}
                                      onChange={({ target }) =>
                                        setEditedPolicy({
                                          ...editedPolicy,
                                          ["quotaLimit"]: target.checked,
                                        })
                                      }
                                    />
                                  </div>
                                </div>
                              ) : null}

                              {quotaLimit && quota ? (
                                <div id="poliFlowQuotaRatesShow">
                                  <div id="policyQuotaDownlink">
                                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                                      <NumberInputWithSpinner
                                        name="quotad"
                                        label="Downlink"
                                        min={0}
                                        max={100000}
                                        step={0.001}
                                        initialValue={quotad}
                                        formatInput={(d) => d3.format(".3f")(d)}
                                        units="Mbps"
                                        onChange={(value) =>
                                          setEditedPolicy({
                                            ...editedPolicy,
                                            ["quotad"]: value,
                                          })
                                        }
                                        onReset={() => {
                                          setEditedPolicy({
                                            ...editedPolicy,
                                            ["quotad"]: DEFAULT_POLICY.quotad,
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div id="policyQuotaUplink">
                                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
                                      <NumberInputWithSpinner
                                        name="quotau"
                                        label="Uplink"
                                        min={0}
                                        max={100000}
                                        step={0.001}
                                        initialValue={quotau}
                                        formatInput={(d) => d3.format(".3f")(d)}
                                        units="Mbps"
                                        onChange={(value) =>
                                          setEditedPolicy({
                                            ...editedPolicy,
                                            ["quotau"]: value,
                                          })
                                        }
                                        onReset={() => {
                                          setEditedPolicy({
                                            ...editedPolicy,
                                            ["quotau"]: DEFAULT_POLICY.quotau,
                                          });
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          </CollapsablePanel>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 margin-0">
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 margin-0 padding-0">
                        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                          <div
                            className="input-group"
                            style={{ paddingTop: "5px" }}
                          >
                            <span
                              className="input-group-addon align-left"
                              title="Uplink shaping"
                            >
                              Skip subscriber rate limitation:{" "}
                              <span id="statusSkip" className="font-bold">
                                {skiprate ? "enabled" : "disabled"}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-3 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                          <Switch
                            className="sw-input"
                            checked={skiprate}
                            onChange={({ target }) =>
                              setEditedPolicy({
                                ...editedPolicy,
                                ["skiprate"]: target.checked,
                              })
                            }
                          />
                        </div>
                        <div className="col-lg-1 col-md-1 col-sm-0 col-xs-0 margin-0 padding-0"></div>
                      </div>
                      <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 margin-0 padding-0">
                        <div className="col-lg-8 col-md-8 col-sm-8 col-xs-8 margin-b-0 padding-0">
                          <div
                            className="input-group"
                            style={{ paddingTop: "5px" }}
                          >
                            <span
                              className="input-group-addon align-left"
                              title="Uplink shaping"
                            >
                              Skip subscriber-group rate limitation:{" "}
                              <span className="font-bold">
                                {skipsubsgrouprate ? "enabled" : "disabled"}
                              </span>
                            </span>
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-4 col-sm-4 col-xs-4 margin-b-0 align-right padding-0">
                          <Switch
                            className="sw-input"
                            checked={skipsubsgrouprate}
                            onChange={({ target }) =>
                              setEditedPolicy({
                                ...editedPolicy,
                                ["skipsubsgrouprate"]: target.checked,
                              })
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </Wrapper>
                )}
              </div>
              <ChangesWrapper>
                <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 margin-b-0 padding-0">
                  {changes ? (
                    <PendingChanges>
                      <Circle />
                      <b>Pending Changes</b>
                    </PendingChanges>
                  ) : null}
                </div>
                <div className="col-lg-6 col-md-6 col-sm-6 col-xs-6 margin-b-0 align-right padding-0">
                  <div className="align-right margin-b-0">
                    <button
                      type="button"
                      className="btn btn-link waves-effect hidden-to-operators"
                      onClick={handleApply}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="btn btn-link waves-effect"
                      onClick={() => globalNavigateBack()}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </ChangesWrapper>
            </div>
          </div>
        </div>
      </div>
      {showBlockModal ? (
        <BlockModal
          onClose={() => setShowBlockModal(false)}
          onApply={() =>
            setEditedPolicy({ ...editedPolicy, ["block"]: !editedPolicy.block })
          }
        />
      ) : null}
    </Wrapper>
  );
};

export default PolicyFlowEdit;
